/**
 * WebSocket event type discriminator.
 * Maps to Python WebSocketEventType enum values.
 */
export type WebSocketEventType =
  | 'changed'
  | 'onboarding.connection_verified'
  | 'onboarding.message_sent'
  | 'onboarding.message_retrieved';

/**
 * Entity types for the unified 'changed' event.
 * Maps to Python ChangedEventData.entity values.
 */
export type ChangedEntity =
  | 'topics'
  | 'topic'
  | 'connections'
  | 'connection'
  | 'oauth_clients'
  | 'messages'
  | 'notifications';

/**
 * Data payload for the unified 'changed' event.
 * All fields except entity are optional depending on context.
 */
export interface ChangedEventData {
  entity: ChangedEntity;
  id?: string;
  topicId?: string;
  sequenceNumber?: number;
  reason?: 'disabled';
}

/**
 * Generic WebSocket event wrapper.
 *
 * All events have a `type` discriminator and `data` payload.
 * The optional `detail` field provides additional context (e.g., for topic.changed).
 */
export interface WebSocketEvent<T = unknown> {
  type: WebSocketEventType;
  detail?: string;
  data: T;
}

/**
 * Unified changed event for cache invalidation.
 * Replaces message.created, notification.created, topic.changed, topic.flushed.
 */
export type ChangedEvent = WebSocketEvent<ChangedEventData> & {
  type: 'changed';
};

/**
 * Onboarding event: Connection verified (any connection type).
 */
export type OnboardingConnectionVerifiedEvent = WebSocketEvent<{
  connectionId: string;
  connectionName: string;
  connectionType: string;
  topicId: string;
  topicName: string;
}> & {
  type: 'onboarding.connection_verified';
};

/**
 * Onboarding event: Message sent to topic.
 */
export type OnboardingMessageSentEvent = WebSocketEvent<{
  messageId: string;
  topicId: string;
  contentPreview: string;
  sequenceNumber: number;
}> & {
  type: 'onboarding.message_sent';
};

/**
 * Onboarding event: Message retrieved by connection.
 */
export type OnboardingMessageRetrievedEvent = WebSocketEvent<{
  connectionId: string;
  connectionName: string;
  topicId: string;
  retrievedAt: string;
}> & {
  type: 'onboarding.message_retrieved';
};

/**
 * Union type for exhaustive handling of all WebSocket events.
 */
export type AnyWebSocketEvent =
  | ChangedEvent
  | OnboardingConnectionVerifiedEvent
  | OnboardingMessageSentEvent
  | OnboardingMessageRetrievedEvent;

/**
 * Type guard to check if an event is an OnboardingConnectionVerifiedEvent.
 */
export function isOnboardingConnectionVerifiedEvent(
  event: AnyWebSocketEvent
): event is OnboardingConnectionVerifiedEvent {
  return event.type === 'onboarding.connection_verified';
}

/**
 * Type guard to check if an event is an OnboardingMessageSentEvent.
 */
export function isOnboardingMessageSentEvent(
  event: AnyWebSocketEvent
): event is OnboardingMessageSentEvent {
  return event.type === 'onboarding.message_sent';
}

/**
 * Type guard to check if an event is an OnboardingMessageRetrievedEvent.
 */
export function isOnboardingMessageRetrievedEvent(
  event: AnyWebSocketEvent
): event is OnboardingMessageRetrievedEvent {
  return event.type === 'onboarding.message_retrieved';
}
